import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import {
    makeStyles,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@material-ui/core";

import {
    Search as SearchIcon,
    DeleteOutline as DeleteOutlineIcon,
    Edit as EditIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import EmailTemplateModal from "../../components/EmailTemplateModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_TEMPLATES") {
        const templates = action.payload;
        const newTemplates = [];

        templates.forEach((template) => {
            const templateIndex = state.findIndex((t) => t.id === template.id);
            if (templateIndex !== -1) {
                state[templateIndex] = template;
            } else {
                newTemplates.push(template);
            }
        });

        return [...state, ...newTemplates];
    }

    if (action.type === "UPDATE_TEMPLATES") {
        const template = action.payload;
        const templateIndex = state.findIndex((t) => t.id === template.id);

        if (templateIndex !== -1) {
            state[templateIndex] = template;
            return [...state];
        } else {
            return [template, ...state];
        }
    }

    if (action.type === "DELETE_TEMPLATE") {
        const templateId = action.payload;

        const templateIndex = state.findIndex((t) => t.id === templateId);
        if (templateIndex !== -1) {
            state.splice(templateIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const EmailTemplates = () => {
    const classes = useStyles();
    const history = useHistory();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(null);
    const [emailTemplateModalOpen, setEmailTemplateModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [templates, dispatch] = useReducer(reducer, []);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchTemplates = async () => {
                try {
                    const { data } = await api.get("/email-templates", {
                        params: { searchParam, pageNumber },
                    });
                    dispatch({ type: "LOAD_TEMPLATES", payload: data });
                    setHasMore(data.hasMore);
                    setLoading(false);
                } catch (err) {
                    toastError(err);
                    setLoading(false);
                }
            };
            fetchTemplates();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber]);

    const handleOpenEmailTemplateModal = () => {
        setSelectedTemplate(null);
        setEmailTemplateModalOpen(true);
    };

    const handleCloseEmailTemplateModal = () => {
        setSelectedTemplate(null);
        setEmailTemplateModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditTemplate = (template) => {
        setSelectedTemplate(template);
        setEmailTemplateModalOpen(true);
    };

    const handleDeleteTemplate = async (templateId) => {
        try {
            await api.delete(`/email-templates/${templateId}`);
            dispatch({ type: "DELETE_TEMPLATE", payload: templateId });
            toast.success(i18n.t("Template deleted successfully")); // TODO: Translate
        } catch (err) {
            toastError(err);
        }
        setDeletingTemplate(null);
        setSearchParam("");
        setPageNumber(1);
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingTemplate && `${i18n.t("Are you sure you want to delete this template?")}`} // TODO: Translate
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteTemplate(deletingTemplate.id)}
            >
                {i18n.t("This action cannot be undone.")}
            </ConfirmationModal>
            <EmailTemplateModal
                open={emailTemplateModalOpen}
                onClose={handleCloseEmailTemplateModal}
                aria-labelledby="form-dialog-title"
                emailTemplateId={selectedTemplate && selectedTemplate.id}
                onSave={(data) => {
                    dispatch({ type: "UPDATE_TEMPLATES", payload: data });
                }}
            />
            <MainHeader>
                <Title>Templates de E-mail</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contracts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenEmailTemplateModal}
                    >
                        Adicionar Template
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper
                className={classes.mainPaper}
                variant="outlined"
                onScroll={handleScroll}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="center">Nome</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {templates.map((template) => (
                                <TableRow key={template.id}>
                                    <TableCell align="center">{template.id}</TableCell>
                                    <TableCell align="center">{template.name}</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditTemplate(template)}
                                        >
                                            <EditIcon />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setConfirmModalOpen(true);
                                                setDeletingTemplate(template);
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={3} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default EmailTemplates;
